import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  AccountSettingsQuery,
  Maybe,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { Loading } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

export * from './fragments'

export type State = {
  loading: boolean
  user: AccountSettingsQuery['user']
  artistId: string
  paymentMethod: Maybe<PaymentMethod>
  formattedExpiredAt: Maybe<string>
  voucherUrl: Maybe<string>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ArtistMembershipCompletionTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('artistMembershipCompletion')
  const { routerPages, generatePath, routerNames, router } = useRouter<{
    status: string
  }>()

  // 個人情報入力が必要な場合に
  // メンバーシップ登録完了画面を表示しないために用いる
  const [isWaitingForPersonalInfo, setIsWaitingForPersonalInfo] = useState(true)

  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // メンバーシップ利用のためお客様情報の登録が必要だった場合に
  // お客様情報入力用のモーダルを表示する処理
  const { showModal } = useSettingUserInfoModal()
  useEffect(() => {
    ;(async () => {
      // キャリア決済の成功時以外は何もしない
      // キャリア決済の成功時はクエリパラメータに status=success が付与される
      if (!state.user || router.query.status !== 'success') return
      setIsWaitingForPersonalInfo(false)
      if (state.user.isMembershipPersonalInformationRequired) {
        showModal({
          onClose() {
            // 入力を完了せず入力モーダルを閉じてしまった場合
            // アーティストトップ画面に遷移する
            router.push(
              generatePath(routerPages.ArtistTop, {
                artistId: state.artistId,
              })
            )
          },
        })
      }
    })()
  }, [state.user, router.query.status])

  return (
    <ArtistThemeProvider>
      <Head
        title={
          state.paymentMethod === PaymentMethod.Konbini
            ? routerNames.ArtistMembershipKonbiniCompletion
            : routerNames.ArtistMembershipCompletion
        }
      />
      <BaseLayout
        margin
        header={{
          title: t(`${state.paymentMethod}Title`),
          left: (
            <IconButton.Close
              edge="start"
              onClick={() => {
                router.goBack()
              }}
            />
          ),
        }}
      >
        {state.loading ||
        // キャリア決済かつ個人情報入力が必要な場合はローディングを表示する
        (router.query.status === 'success' && isWaitingForPersonalInfo) ? (
          <LoadingCell />
        ) : (
          <>
            <Box sx={{ pt: 7, pb: 6 }}>
              <Typography variant="h5" align="center">
                {t(`${state.paymentMethod}SuccessTitle`)}
              </Typography>
            </Box>
            <Typography variant="body2">
              {t(`${state.paymentMethod}SuccessDesc`)}
            </Typography>
            {state.paymentMethod === PaymentMethod.Konbini && (
              <Stack
                sx={{
                  mt: 5,
                  py: 2,
                  borderColor: 'error.main',
                  borderWidth: 1,
                  borderStyle: 'solid',
                }}
              >
                <Typography variant="subtitle1" color="error" align="center">
                  {t('paymentDeadline', {
                    date: state.formattedExpiredAt ?? '',
                  })}
                </Typography>
              </Stack>
            )}
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ py: 5, mt: 5 }}
            >
              {state.paymentMethod === PaymentMethod.CreditCard ? (
                <>
                  <Button
                    size="large"
                    href={routerPages.MembershipManagement}
                    sx={{ minWidth: 244 }}
                  >
                    {t('viewMembershipManagement', 'メンバーシップ管理を見る')}
                  </Button>
                  <Button
                    variant="text"
                    href={generatePath(routerPages.ArtistTop, {
                      artistId: state.artistId,
                    })}
                    sx={{ minWidth: 244 }}
                  >
                    {t('viewTOP', 'TOPページへ')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    component="a"
                    target="_blank"
                    size="large"
                    href={state.voucherUrl ?? '#'}
                    sx={{ minWidth: 244 }}
                  >
                    {t('proceedPaymentProcedure', '決済手順へすすむ')}
                  </Button>
                  <Button
                    variant="text"
                    href={routerPages.PaymentStatuses}
                    sx={{ minWidth: 244 }}
                  >
                    {t('checkPaymentStatus', '決済状況を確認する')}
                  </Button>
                </>
              )}
            </Stack>
          </>
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const LoadingCell: Cell = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '90dvh',
      }}
    >
      <Loading />
    </Box>
  )
}
